import * as React from 'react';

import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { LucideIcon } from 'lucide-react';

import { getFontFamily } from 'services/FontService';
import { IColors } from 'types';
import { Colors } from 'services/ColorService';
import { HEADER_COMPACT_MEDIA } from 'components/constants';
import { Icon, IconsNames } from 'atoms';

export interface IMenuItem extends IMenuItemStyle {
  children: string;
  to: string;
  /**
   * Значок пункта (lucide). Рисуется у пунктов выпадающего списка и у
   * пункта-одиночки в шапке; у заголовка группы его нет — шесть групп со
   * значками не помещаются в шапку на 1280 px.
   */
  icon?: LucideIcon;
}

export interface IMenuItemStyle {
  colors?: IColors;
  isActive?: boolean;
  disabled?: boolean;
  isMulti?: IMenuItem[];
}

export const MenuItem = ({
  to,
  children,
  icon: ItemIcon,
  colors = { defaultColor: '#263446', activeColor: '#88959E' },
  isActive,
  disabled,
  isMulti,
}: IMenuItem) => (
  <StyledMenuItem
    isActive={isActive}
    colors={colors}
    disabled={disabled}
    isMulti={isMulti}
  >
    <StyledLink to={to}>
      {ItemIcon ? (
        <ItemIcon size={15} strokeWidth={2} aria-hidden="true" />
      ) : null}
      <span>{children}</span>
      {isMulti ? (
        <IconWrapper>
          <Icon
            name={IconsNames.ArrowIcon}
            width={10}
            height={6}
            color={Colors.Blue}
          />
        </IconWrapper>
      ) : null}
    </StyledLink>
    {isMulti ? (
      <SubItemList>
        {isMulti.map(({ children: text, to: itemTo, icon: SubIcon }) => (
          <SubItem key={`${children}-${text}`}>
            <StyledLink to={itemTo}>
              {SubIcon ? (
                <SubIcon size={15} strokeWidth={2} aria-hidden="true" />
              ) : null}
              <span>{text}</span>
            </StyledLink>
          </SubItem>
        ))}
      </SubItemList>
    ) : null}
  </StyledMenuItem>
);

/**
 * Ссылка пункта. `nowrap`: подпись группы не переносится на вторую строку,
 * а список растёт по самому длинному пункту. Поля по бокам — 6 px, а не 8:
 * шесть групп «по задачам» (2026-09-22) должны влезть в шапку на 1280 px.
 */
const StyledLink = styled(Link)`
  box-sizing: border-box;
  padding: 8px 6px;
  font-family: ${getFontFamily('bold')};
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 17px;
  text-align: center;
  letter-spacing: 0.5px;
  text-decoration: none;
  white-space: nowrap;

  ${HEADER_COMPACT_MEDIA} {
    padding: 8px 4px;
    font-size: 13px;
    letter-spacing: 0;
  }
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${Colors.Black};

  & > svg {
    flex: none;
  }
`;

const SubItemList = styled.ul`
  position: absolute;
  top: calc(100% - 8px);
  left: 0;
  display: flex;
  flex-direction: column;
  min-width: 230px;
  border: 1px solid ${Colors.LightGray};
  box-shadow: 0px 4px 8px ${Colors.LightShadow};
  list-style: none;
  opacity: 0;
  visibility: hidden;
  transition: visibility 0s 0.15s, top 0s 0.15s, opacity 0s 0.15s;
`;

const SubItem = styled.li`
  height: 40px;
  background-color: ${Colors.White};

  /* Двоеточия здесь не было («background-color #…»), и подсветка пункта под
     курсором молча не работала. */
  &:hover {
    background-color: ${Colors.MainBackground};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${Colors.LightGray};
  }

  & > ${StyledLink} {
    box-sizing: border-box;
    height: 100%;
    width: 100%;
    padding: 0 16px;
    gap: 10px;
    font-family: ${getFontFamily('medium')};
    font-weight: normal;
    color: ${Colors.Black};
  }

  & > ${StyledLink} > svg {
    color: ${Colors.Gray};
  }

  &:hover > ${StyledLink} > svg {
    color: ${Colors.Blue};
  }
`;

/** Стрелка группы: 10 px без полей — отступ от подписи даёт `gap` ссылки. */
const IconWrapper = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 16px;
  width: 10px;
  transform: rotate(-180deg);
  transition: transform 0.25s ease-in-out 0.15s;
`;

const StyledMenuItem = styled.li<IMenuItemStyle>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  z-index: 1;

  ${({ isMulti }) =>
    isMulti
      ? `border: 1px solid;
       border-color: transparent;
       transition: border-color 0s 0.15s;
    `
      : ``}

  & > ${StyledLink} {
    color: ${({ colors, isActive }) =>
      isActive ? colors?.activeColor : colors?.defaultColor};
    opacity: ${({ disabled }) => (disabled ? '0.1' : '1')};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  }

  /* 16, а не 32: шесть групп меню помещаются в шапку на 1280 px. */
  &:not(:last-child) {
    margin-right: 16px;

    ${HEADER_COMPACT_MEDIA} {
      margin-right: 8px;
    }
  }

  &:hover {
    ${({ isMulti }) =>
      isMulti
        ? `
          border-color: ${Colors.LightGray};
          transition: border-color 0s 0s;

          ${SubItemList} {
            top: calc(100% + 8px);
            opacity: 1;
            visibility: visible;
            transition: visibility 0s 0s, top 0.25s ease-in-out,
              opacity 0.3s ease-in-out;
          }

          ${IconWrapper} {
            transform: rotate(0);
            transition: transform 0.25s ease-in-out;
          }
        `
        : ``}
  }
`;
